import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Plus, Edit, Save, Download, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

interface AssessmentFormBuilderProps {
  moduleId: string;
  onFormCreated: () => void;
}

interface Question {
  id?: string;
  question_en: string;
  question_el: string;
  question_type: 'single' | 'multiple';
  options: {
    choices: Array<{
      id: string;
      text_en: string;
      text_el: string;
    }>;
    correct_answers: string[];
  };
  points: number;
  order_index: number;
}

interface AssessmentForm {
  id?: string;
  title_en: string;
  title_el: string;
  description_en: string;
  description_el: string;
  pass_percentage: number;
  is_active: boolean;
}

export const AssessmentFormBuilder = ({ moduleId, onFormCreated }: AssessmentFormBuilderProps) => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState<AssessmentForm>({
    title_en: '',
    title_el: '',
    description_en: '',
    description_el: '',
    pass_percentage: 70,
    is_active: true
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assessmentFormId, setAssessmentFormId] = useState<string | null>(null);

  useEffect(() => {
    fetchExistingForm();
  }, [moduleId]);

  const downloadTemplate = () => {
    // Row-oriented template: Row 1 = headers, Row 2+ = questions
    const headers = [
      'Question (English)',
      'Question (Greek)', 
      'Question Type (single/multiple/text/boolean)',
      'Option A (English)',
      'Option A (Greek)',
      'Option B (English)',
      'Option B (Greek)',
      'Option C (English)',
      'Option C (Greek)',
      'Option D (English)',
      'Option D (Greek)',
      'Correct Answer (e.g., A or A,B,C)',
      'Points'
    ];
    
    const exampleQuestions = [
      ['What is 2+2?', 'Τι είναι 2+2;', 'single', '3', '3', '4', '4', '5', '5', '6', '6', 'B', '10'],
      ['Select all prime numbers', 'Επιλέξτε όλους τους πρώτους αριθμούς', 'multiple', '2', '2', '3', '3', '4', '4', '5', '5', 'A,B,D', '10']
    ];
    
    const template = [headers, ...exampleQuestions];
    // Create Excel-compatible format using tab-separated values
    const excelContent = template.map(row => row.join('\t')).join('\n');
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assessment_questions_template.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully');
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const rows = text.split('\n').slice(1);
      const importedQuestions: Question[] = [];

      rows.forEach((row, index) => {
        if (!row.trim()) return;
        // Handle both tab-separated (Excel) and comma-separated (CSV)
        const cols = row.includes('\t') ? row.split('\t') : row.split(',');
        
        if (cols.length < 13) return;

        // Convert letter answers (A,B,C,D) to numbers (1,2,3,4)
        const letterToNumber: {[key: string]: string} = { 'A': '1', 'B': '2', 'C': '3', 'D': '4' };
        const correctAnswersLetters = cols[11].split(',').map(a => a.trim().toUpperCase());
        const correctAnswers = correctAnswersLetters.map(letter => letterToNumber[letter] || letter).filter(a => a);
        
        importedQuestions.push({
          question_en: cols[0],
          question_el: cols[1],
          question_type: cols[2] === 'multiple' ? 'multiple' : 'single',
          options: {
            choices: [
              { id: '1', text_en: cols[3], text_el: cols[4] },
              { id: '2', text_en: cols[5], text_el: cols[6] },
              { id: '3', text_en: cols[7], text_el: cols[8] },
              { id: '4', text_en: cols[9], text_el: cols[10] }
            ].filter(c => c.text_en && c.text_el),
            correct_answers: correctAnswers
          },
          points: parseInt(cols[12]) || 10,
          order_index: questions.length + index
        });
      });

      setQuestions([...questions, ...importedQuestions]);
      toast.success(`Imported ${importedQuestions.length} questions successfully`);
    } catch (error) {
      console.error('Error importing questions:', error);
      toast.error('Failed to import questions. Please check the file format.');
    }
  };

  const fetchExistingForm = async () => {
    try {
      const { data: form } = await supabase
        .from('assessment_forms')
        .select('*')
        .eq('module_id', moduleId)
        .maybeSingle();

      if (form) {
        setFormData(form);
        setAssessmentFormId(form.id);
        
        const { data: questionsData } = await supabase
          .from('assessment_questions')
          .select('*')
          .eq('form_id', form.id)
          .order('order_index');

        if (questionsData) {
          setQuestions(questionsData.map(q => ({
            id: q.id,
            question_en: q.question_en,
            question_el: q.question_el,
            question_type: q.question_type as 'single' | 'multiple',
            options: q.options as any,
            points: q.points,
            order_index: q.order_index
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching form:', error);
    }
  };

  const saveAssessmentForm = async () => {
    setIsSaving(true);
    try {
      let formId = assessmentFormId;
      
      if (!formId) {
        console.log('Saving assessment with moduleId:', moduleId);
        console.log('Form data:', formData);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        // Prepare the data to insert
        const insertData = {
            module_id: moduleId,
            title: formData.title_en || formData.title_el || 'Untitled Assessment',
            description: formData.description_en || formData.description_el || '',
            passing_score: formData.pass_percentage || 70,
            is_active: formData.is_active !== false, // Ensure boolean
            created_by: user?.id
        };
        
        console.log('Inserting data:', insertData);
        
        const { data: newForm, error } = await supabase
          .from('assessment_forms')
          .insert(insertData)
          .select()
          .single();

        if (error) throw error;
        formId = newForm.id;
        setAssessmentFormId(formId);
      } else {
        const { error } = await supabase
          .from('assessment_forms')
          .update(formData)
          .eq('id', formId);

        if (error) throw error;
      }

      // Delete existing questions first, then insert all new ones to avoid conflicts
      if (formId) {
        await supabase
          .from('assessment_questions')
          .delete()
          .eq('form_id', formId);
      }

      // Insert all questions as new
      const questionsToInsert = questions.map((question, index) => ({
        form_id: formId,
        question_text: question.question_en || question.question_el || '',
        question_type: question.question_type,
        options: question.options,
        points: question.points,
        sort_order: index
      }));

      if (questionsToInsert.length > 0) {
        const { error: questionsError } = await supabase
          .from('assessment_questions')
          .insert(questionsToInsert);

        if (questionsError) {
          console.error('Questions error:', questionsError);
          throw questionsError;
        }
      }

      toast.success('Assessment form saved successfully!');
      onFormCreated();
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save assessment form');
    } finally {
      setIsSaving(false);
    }
  };

  const addQuestion = () => {
    setEditingQuestion({
      question_en: '',
      question_el: '',
      question_type: 'single',
      options: {
        choices: [
          { id: 'a', text_en: '', text_el: '' },
          { id: 'b', text_en: '', text_el: '' }
        ],
        correct_answers: []
      },
      points: 1,
      order_index: questions.length
    });
    setIsDialogOpen(true);
  };

  const editQuestion = (question: Question) => {
    setEditingQuestion({ ...question });
    setIsDialogOpen(true);
  };

  const saveQuestion = () => {
    if (!editingQuestion) return;

    if (editingQuestion.id) {
      setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? editingQuestion : q));
    } else {
      setQuestions(prev => [...prev, { ...editingQuestion, id: `temp-${Date.now()}` }]);
    }

    setIsDialogOpen(false);
    setEditingQuestion(null);
  };

  const deleteQuestion = (questionId: string) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const addChoice = () => {
    if (!editingQuestion) return;
    
    const nextId = String.fromCharCode(97 + editingQuestion.options.choices.length); // a, b, c, d...
    setEditingQuestion({
      ...editingQuestion,
      options: {
        ...editingQuestion.options,
        choices: [
          ...editingQuestion.options.choices,
          { id: nextId, text_en: '', text_el: '' }
        ]
      }
    });
  };

  const removeChoice = (choiceId: string) => {
    if (!editingQuestion || editingQuestion.options.choices.length <= 2) return;

    setEditingQuestion({
      ...editingQuestion,
      options: {
        choices: editingQuestion.options.choices.filter(c => c.id !== choiceId),
        correct_answers: editingQuestion.options.correct_answers.filter(id => id !== choiceId)
      }
    });
  };

  const updateChoice = (choiceId: string, field: 'text_en' | 'text_el', value: string) => {
    if (!editingQuestion) return;

    setEditingQuestion({
      ...editingQuestion,
      options: {
        ...editingQuestion.options,
        choices: editingQuestion.options.choices.map(c =>
          c.id === choiceId ? { ...c, [field]: value } : c
        )
      }
    });
  };

  const toggleCorrectAnswer = (choiceId: string) => {
    if (!editingQuestion) return;

    const isCorrect = editingQuestion.options.correct_answers.includes(choiceId);
    let newCorrectAnswers;

    if (editingQuestion.question_type === 'single') {
      newCorrectAnswers = isCorrect ? [] : [choiceId];
    } else {
      newCorrectAnswers = isCorrect
        ? editingQuestion.options.correct_answers.filter(id => id !== choiceId)
        : [...editingQuestion.options.correct_answers, choiceId];
    }

    setEditingQuestion({
      ...editingQuestion,
      options: {
        ...editingQuestion.options,
        correct_answers: newCorrectAnswers
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Assessment Form Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title_en">Title (English)</Label>
              <Input
                id="title_en"
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                placeholder="Enter title in English"
              />
            </div>
            <div>
              <Label htmlFor="title_el">Title (Greek)</Label>
              <Input
                id="title_el"
                value={formData.title_el}
                onChange={(e) => setFormData({ ...formData, title_el: e.target.value })}
                placeholder="Enter title in Greek"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="description_en">Description (English)</Label>
              <Textarea
                id="description_en"
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                placeholder="Enter description in English"
              />
            </div>
            <div>
              <Label htmlFor="description_el">Description (Greek)</Label>
              <Textarea
                id="description_el"
                value={formData.description_el}
                onChange={(e) => setFormData({ ...formData, description_el: e.target.value })}
                placeholder="Enter description in Greek"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pass_percentage">Pass Percentage</Label>
              <Input
                id="pass_percentage"
                type="number"
                min="0"
                max="100"
                value={formData.pass_percentage}
                onChange={(e) => setFormData({ ...formData, pass_percentage: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Questions ({questions.length})</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Download Template' : 'Λήψη Προτύπου'}
            </Button>
            <Button variant="outline" onClick={() => document.getElementById('bulk-import')?.click()}>
              <Upload className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Bulk Import' : 'Μαζική Εισαγωγή'}
            </Button>
            <input
              id="bulk-import"
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={handleBulkImport}
              className="hidden"
            />
            <Button onClick={addQuestion}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Add Question' : 'Προσθήκη Ερώτησης'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {questions.map((question, index) => (
              <Card key={question.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">Q{index + 1}</Badge>
                      <Badge variant={question.question_type === 'single' ? 'default' : 'secondary'}>
                        {question.question_type === 'single' ? 'Single Answer' : 'Multiple Answers'}
                      </Badge>
                      <Badge variant="outline">{question.points} pts</Badge>
                    </div>
                    <p className="font-medium mb-1">
                      {language === 'en' ? question.question_en : question.question_el}
                    </p>
                    <div className="text-sm text-muted-foreground">
                      {question.options.choices.map(choice => (
                        <div key={choice.id} className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${
                            question.options.correct_answers.includes(choice.id) 
                              ? 'bg-green-500' 
                              : 'bg-gray-300'
                          }`} />
                          {language === 'en' ? choice.text_en : choice.text_el}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => editQuestion(question)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteQuestion(question.id!)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={saveAssessmentForm} disabled={isSaving}>
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Assessment Form'}
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion?.id ? 'Edit Question' : 'Add New Question'}
            </DialogTitle>
          </DialogHeader>
          
          {editingQuestion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="question_en">Question (English)</Label>
                  <Textarea
                    id="question_en"
                    value={editingQuestion.question_en}
                    onChange={(e) => setEditingQuestion({
                      ...editingQuestion,
                      question_en: e.target.value
                    })}
                    placeholder="Enter question in English"
                  />
                </div>
                <div>
                  <Label htmlFor="question_el">Question (Greek)</Label>
                  <Textarea
                    id="question_el"
                    value={editingQuestion.question_el}
                    onChange={(e) => setEditingQuestion({
                      ...editingQuestion,
                      question_el: e.target.value
                    })}
                    placeholder="Enter question in Greek"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="question_type">Question Type</Label>
                  <Select
                    value={editingQuestion.question_type}
                    onValueChange={(value: 'single' | 'multiple') => setEditingQuestion({
                      ...editingQuestion,
                      question_type: value,
                      options: {
                        ...editingQuestion.options,
                        correct_answers: value === 'single' 
                          ? editingQuestion.options.correct_answers.slice(0, 1)
                          : editingQuestion.options.correct_answers
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Answer</SelectItem>
                      <SelectItem value="multiple">Multiple Answers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="points">Points</Label>
                  <Input
                    id="points"
                    type="number"
                    min="1"
                    value={editingQuestion.points}
                    onChange={(e) => setEditingQuestion({
                      ...editingQuestion,
                      points: parseInt(e.target.value) || 1
                    })}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Answer Choices</Label>
                  <Button variant="outline" size="sm" onClick={addChoice}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Choice
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {editingQuestion.options.choices.map((choice) => (
                    <div key={choice.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{choice.id.toUpperCase()}</Badge>
                          {editingQuestion.question_type === 'single' ? (
                            <RadioGroup
                              value={editingQuestion.options.correct_answers[0] || ''}
                              onValueChange={(value) => toggleCorrectAnswer(value)}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value={choice.id} id={`correct-${choice.id}`} />
                                <Label htmlFor={`correct-${choice.id}`}>Correct Answer</Label>
                              </div>
                            </RadioGroup>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`correct-${choice.id}`}
                                checked={editingQuestion.options.correct_answers.includes(choice.id)}
                                onCheckedChange={() => toggleCorrectAnswer(choice.id)}
                              />
                              <Label htmlFor={`correct-${choice.id}`}>Correct Answer</Label>
                            </div>
                          )}
                        </div>
                        {editingQuestion.options.choices.length > 2 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeChoice(choice.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor={`choice-en-${choice.id}`}>Text (English)</Label>
                          <Input
                            id={`choice-en-${choice.id}`}
                            value={choice.text_en}
                            onChange={(e) => updateChoice(choice.id, 'text_en', e.target.value)}
                            placeholder="Enter choice text in English"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`choice-el-${choice.id}`}>Text (Greek)</Label>
                          <Input
                            id={`choice-el-${choice.id}`}
                            value={choice.text_el}
                            onChange={(e) => updateChoice(choice.id, 'text_el', e.target.value)}
                            placeholder="Enter choice text in Greek"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveQuestion}>
                  Save Question
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};